import { BookOpen, ExternalLink, Plus, Search, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { formatDate } from '../../model/dates';
import { newId } from '../../model/people';
import { citationCounts, mediaOfSource, sourceUsage } from '../../model/research';
import { removeSource, upsertSource } from '../../model/tree';
import type { Id, Source } from '../../model/types';
import { targetLabel } from '../components/CitationEditor';
import { Empty, Field, Modal } from '../components/common';
import { DateInput } from '../components/DateInput';
import { PlaceInput } from '../components/PlaceInput';
import { QUALITY_LABELS, SOURCE_KIND_SUGGESTIONS } from '../labels';
import { closeDialog, commit, confirmAction, getState, openDialog, toast, useApp } from '../store';
import { MediaThumb } from './MediaView';

function plural(n: number, one: string, many = one + 's'): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;
}

export function SourcesView() {
  const tree = useApp((s) => s.tree)!;
  const [q, setQ] = useState('');
  const counts = useMemo(() => citationCounts(tree), [tree]);
  const sources = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return Object.values(tree.sources)
      .filter((s) => !needle || [s.title, s.author, s.publication, s.kind, s.repository, s.place].join(' ').toLowerCase().includes(needle))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [tree, q]);
  const total = Object.keys(tree.sources).length;

  return (
    <div className="page">
      <div className="page-inner" style={{ maxWidth: 1000 }}>
        <div className="page-head">
          <div>
            <h1 style={{ fontSize: 22 }}>Sources</h1>
            <p className="muted" style={{ marginTop: 4 }}>Records, books, interviews, and websites, and what each one backs up.</p>
          </div>
          <button className="btn primary" onClick={() => openDialog({ type: 'edit-source', sourceId: null })}>
            <Plus size={15} /> New source
          </button>
        </div>
        {total > 0 && (
          <label className="search-box">
            <Search size={15} />
            <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles, authors, kinds, and repositories" aria-label="Search sources" />
          </label>
        )}
        {total === 0 ? (
          <div className="card-box">
            <Empty icon={<BookOpen size={28} />} title="No sources yet">
              Add the records and documents your research rests on, then cite them from anyone’s details.
            </Empty>
          </div>
        ) : sources.length === 0 ? (
          <p className="muted">Nothing matches.</p>
        ) : (
          <div className="card-box list-box">
            {sources.map((s) => {
              const n = counts.get(s.id) ?? 0;
              return (
                <button key={s.id} className="list-row" onClick={() => openDialog({ type: 'edit-source', sourceId: s.id })}>
                  <BookOpen size={18} className="faint" />
                  <span className="grow">
                    <span className="title">{s.title}</span>
                    <span className="meta">{[s.author, s.kind, s.publication, formatDate(s.date), s.repository].filter(Boolean).join(' · ')}</span>
                  </span>
                  <span className="badge">{n ? `Cited ${plural(n, 'time')}` : 'Not cited'}</span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/** Create or edit a source, and see everything it backs up. */
export function SourceEditor({ sourceId, onSave }: { sourceId: Id | null; onSave?: (id: Id) => void }) {
  const tree = useApp((s) => s.tree)!;
  const existing = sourceId ? tree.sources[sourceId] : undefined;
  const [s, setS] = useState<Source>(() => structuredClone(existing) ?? { id: newId('s'), title: '' });
  const set = (patch: Partial<Source>) => setS((x) => ({ ...x, ...patch }));
  const usage = existing ? sourceUsage(tree, existing.id) : [];
  const media = existing ? mediaOfSource(tree, existing.id) : [];
  const text = (k: keyof Source) => ({ value: (s[k] as string | undefined) ?? '', onChange: (e: { target: { value: string } }) => set({ [k]: e.target.value || undefined } as Partial<Source>) });

  const save = () => {
    // A new source needs a title, or saving an untouched form leaves a blank record behind.
    if (!existing && !s.title.trim()) return toast('Give the source a title. Everything else is optional.', 'error');
    const clean = { ...s, title: s.title.trim() || 'Untitled source' };
    commit(upsertSource(getState().tree!, clean));
    closeDialog();
    onSave?.(clean.id);
  };

  if (sourceId && !existing) {
    return (
      <Modal title="Source not found" footer={<button className="btn" onClick={closeDialog}>Close</button>}>
        <p className="muted">This source has been deleted.</p>
      </Modal>
    );
  }

  return (
    <Modal
      title={existing ? existing.title : 'New source'}
      wide
      footer={
        <>
          {existing && (
            <button
              className="btn danger left"
              onClick={() =>
                confirmAction({
                  title: `Delete “${existing.title}”?`,
                  body: usage.length ? `It’s cited ${plural(usage.length, 'time')}; those citations are removed too.` : 'Nothing cites it.',
                  confirmLabel: 'Delete source',
                  danger: true,
                  onConfirm: () => {
                    commit(removeSource(getState().tree!, existing.id));
                    closeDialog();
                  },
                })
              }
            >
              <Trash2 size={15} /> Delete
            </button>
          )}
          <button className="btn" onClick={closeDialog}>
            Cancel
          </button>
          <button className="btn primary" onClick={save}>
            Save
          </button>
        </>
      }
    >
      <div className="source-layout">
        <div className="stack">
          <Field label="Title">
            <input className="input" {...text('title')} placeholder="e.g., 1880 U.S. Census, Boston Ward 7" autoFocus={!existing} />
          </Field>
          <div className="grid-2">
            <Field label="Author or creator">
              <input className="input" {...text('author')} />
            </Field>
            <Field label="Kind">
              <input className="input" {...text('kind')} list="source-kinds" placeholder="e.g., Census, Newspaper" />
              <datalist id="source-kinds">
                {SOURCE_KIND_SUGGESTIONS.map((k) => (
                  <option key={k} value={k} />
                ))}
              </datalist>
            </Field>
          </div>
          <Field label="Publication" hint="Publisher, newspaper, collection, or edition">
            <input className="input" {...text('publication')} />
          </Field>
          <div className="grid-2">
            <Field label="Date">
              <DateInput value={s.date} onChange={(date) => set({ date })} />
            </Field>
            <Field label="Place">
              <PlaceInput className="input" value={s.place ?? ''} onChange={(place) => set({ place: place || undefined })} />
            </Field>
          </div>
          <div className="grid-2">
            <Field label="Repository" hint="Archive, library, or website it’s held in">
              <input className="input" {...text('repository')} />
            </Field>
            <Field label="Call number">
              <input className="input" {...text('callNumber')} />
            </Field>
          </div>
          <Field label="Web address">
            <div className="row">
              <input className="input grow" type="url" {...text('url')} placeholder="https://" />
              {s.url && /^https?:\/\//i.test(s.url) && (
                <a className="btn icon" href={s.url} target="_blank" rel="noreferrer noopener" aria-label="Open the web address">
                  <ExternalLink size={15} />
                </a>
              )}
            </div>
          </Field>
          <Field label="Text" hint="A transcription or quotation">
            <textarea className="textarea" style={{ minHeight: 70 }} {...text('text')} />
          </Field>
          <Field label="Notes">
            <textarea className="textarea" style={{ minHeight: 50 }} {...text('notes')} />
          </Field>
        </div>
        {existing && (
          <div className="stack">
            <div className="section-title">Cited by ({usage.length})</div>
            {usage.length ? (
              <div className="stack tight">
                {usage.map((u, i) => {
                  const personId = 'relId' in u.target ? tree.relationships[u.target.relId]?.a : u.target.personId;
                  return (
                    <button
                      key={i}
                      className="list-row compact"
                      onClick={() => personId && openDialog({ type: 'edit-citation', personId, target: u.target, index: u.index })}
                    >
                      <span className="grow">
                        <span className="title">{targetLabel(tree, u.target)}</span>
                        <span className="meta">{[u.citation.page, u.citation.quality !== undefined ? QUALITY_LABELS[u.citation.quality] : ''].filter(Boolean).join(' · ')}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="faint small-text">Nothing cites this source yet. Cite it from someone’s details.</p>
            )}
            {media.length > 0 && (
              <>
                <div className="section-title">Media ({media.length})</div>
                <div className="stack tight">
                  {media.map((m) => (
                    <button key={m.id} className="list-row compact" onClick={() => openDialog({ type: 'media-details', mediaId: m.id })}>
                      <MediaThumb item={m} size="sm" />
                      <span className="grow">
                        <span className="title">{m.title}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

